import { Box, type BoxProps, useColorModeValue } from "@chakra-ui/react";
import { motion, useMotionTemplate, useMotionValue, useReducedMotion } from "framer-motion";
import { useState, type MouseEvent, type ReactNode } from "react";

const MotionBox = motion(Box);

type SpotlightCardProps = BoxProps & {
  children: ReactNode;
};

export function SpotlightCard({ children, ...props }: SpotlightCardProps) {
  const prefersReducedMotion = useReducedMotion();
  const spotlightColor = useColorModeValue("rgba(224,134,44,0.14)", "rgba(232,164,74,0.16)");
  const hoverShadow = useColorModeValue(
    "0 20px 45px rgba(20,20,25,0.09)",
    "0 20px 45px rgba(0,0,0,0.5)"
  );
  const [isHovering, setIsHovering] = useState(false);

  const mouseX = useMotionValue(50);
  const mouseY = useMotionValue(50);

  function handleMouseMove(event: MouseEvent<HTMLDivElement>) {
    if (prefersReducedMotion) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    mouseX.set(((event.clientX - bounds.left) / bounds.width) * 100);
    mouseY.set(((event.clientY - bounds.top) / bounds.height) * 100);
  }

  const background = useMotionTemplate`radial-gradient(220px circle at ${mouseX}% ${mouseY}%, ${spotlightColor}, transparent 70%)`;

  return (
    <MotionBox
      position="relative"
      overflow="hidden"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      transition="transform 0.3s ease, box-shadow 0.3s ease"
      whileHover={prefersReducedMotion ? undefined : { y: -6 }}
      _hover={{ boxShadow: hoverShadow }}
      {...props}
    >
      {!prefersReducedMotion ? (
        <MotionBox
          position="absolute"
          inset={0}
          pointerEvents="none"
          animate={{ opacity: isHovering ? 1 : 0 }}
          transition={{ duration: 0.25 }}
          style={{ background }}
          aria-hidden
        />
      ) : null}
      <Box position="relative">{children}</Box>
    </MotionBox>
  );
}
